/**
 * CRM Zustand store — manages CRM page state.
 */

import { create } from 'zustand'
import type {
  CrmContactSummary,
  CrmContactDetail,
  ContactFilters,
  TimelineEvent,
  Pipeline,
  Deal,
  PipelineStats,
  Segment,
  Activity,
} from '@/types/crm'

interface CrmState {
  // Active tab
  activeTab: string

  // Contact list
  contacts: CrmContactSummary[]
  contactTotal: number
  contactTotalPages: number
  isLoadingContacts: boolean

  // Selected contact
  selectedContact: CrmContactDetail | null
  isLoadingDetail: boolean

  // Timeline
  timelineEvents: TimelineEvent[]
  timelineHasMore: boolean
  timelineNextCursor: string | null
  isLoadingTimeline: boolean

  // Filters
  contactFilters: ContactFilters

  // Activities
  activities: Activity[]
  activityTotal: number
  isLoadingActivities: boolean
  overdueCount: number

  // Segments
  segments: Segment[]
  activeSegment: Segment | null
  isLoadingSegments: boolean

  // Deals
  pipelines: Pipeline[]
  activePipeline: Pipeline | null
  deals: Deal[]
  dealTotal: number
  isLoadingDeals: boolean
  selectedDeal: Deal | null
  pipelineStats: PipelineStats | null
  dealViewMode: 'kanban' | 'list'

  // Setters
  setActiveTab: (tab: string) => void
  setContacts: (contacts: CrmContactSummary[], total: number, totalPages: number) => void
  setLoadingContacts: (loading: boolean) => void
  setSelectedContact: (contact: CrmContactDetail | null) => void
  setLoadingDetail: (loading: boolean) => void
  setTimelineEvents: (events: TimelineEvent[], hasMore: boolean, nextCursor: string | null) => void
  appendTimelineEvents: (events: TimelineEvent[], hasMore: boolean, nextCursor: string | null) => void
  setLoadingTimeline: (loading: boolean) => void
  setContactFilters: (filters: Partial<ContactFilters>) => void
  resetContactFilters: () => void

  // Activity setters
  setActivities: (activities: Activity[], total: number) => void
  setLoadingActivities: (loading: boolean) => void
  setOverdueCount: (count: number) => void

  // Segment setters
  setSegments: (segments: Segment[]) => void
  setActiveSegment: (segment: Segment | null) => void
  setLoadingSegments: (loading: boolean) => void

  // Deal setters
  setPipelines: (pipelines: Pipeline[]) => void
  setActivePipeline: (pipeline: Pipeline | null) => void
  setDeals: (deals: Deal[], total: number) => void
  setLoadingDeals: (loading: boolean) => void
  setSelectedDeal: (deal: Deal | null) => void
  setPipelineStats: (stats: PipelineStats | null) => void
  setDealViewMode: (mode: 'kanban' | 'list') => void
  updateDealInList: (deal: Deal) => void

  // Reset
  reset: () => void
}

const initialFilters: ContactFilters = {
  search: '',
  channel: null,
  tag: null,
  blocked: null,
  includeDeleted: false,
  sortBy: 'last_seen',
  sortOrder: 'desc',
  page: 1,
  perPage: 50,
}

const initialState = {
  activeTab: 'contacts',
  contacts: [] as CrmContactSummary[],
  contactTotal: 0,
  contactTotalPages: 1,
  isLoadingContacts: false,
  selectedContact: null as CrmContactDetail | null,
  isLoadingDetail: false,
  timelineEvents: [] as TimelineEvent[],
  timelineHasMore: false,
  timelineNextCursor: null as string | null,
  isLoadingTimeline: false,
  contactFilters: { ...initialFilters },
  activities: [] as Activity[],
  activityTotal: 0,
  isLoadingActivities: false,
  overdueCount: 0,
  segments: [] as Segment[],
  activeSegment: null as Segment | null,
  isLoadingSegments: false,
  pipelines: [] as Pipeline[],
  activePipeline: null as Pipeline | null,
  deals: [] as Deal[],
  dealTotal: 0,
  isLoadingDeals: false,
  selectedDeal: null as Deal | null,
  pipelineStats: null as PipelineStats | null,
  dealViewMode: 'kanban' as const,
}

export const useCrmStore = create<CrmState>()((set) => ({
  ...initialState,

  setActiveTab: (tab) => set({ activeTab: tab }),

  setContacts: (contacts, total, totalPages) =>
    set({ contacts, contactTotal: total, contactTotalPages: totalPages }),

  setLoadingContacts: (loading) => set({ isLoadingContacts: loading }),

  setSelectedContact: (contact) => set({ selectedContact: contact }),

  setLoadingDetail: (loading) => set({ isLoadingDetail: loading }),

  setTimelineEvents: (events, hasMore, nextCursor) =>
    set({ timelineEvents: events, timelineHasMore: hasMore, timelineNextCursor: nextCursor }),

  appendTimelineEvents: (events, hasMore, nextCursor) =>
    set((state) => ({
      timelineEvents: [...state.timelineEvents, ...events],
      timelineHasMore: hasMore,
      timelineNextCursor: nextCursor,
    })),

  setLoadingTimeline: (loading) => set({ isLoadingTimeline: loading }),

  setContactFilters: (filters) =>
    set((state) => ({
      contactFilters: { ...state.contactFilters, ...filters },
    })),

  resetContactFilters: () => set({ contactFilters: { ...initialFilters } }),

  // Activity setters
  setActivities: (activities, total) => set({ activities, activityTotal: total }),
  setLoadingActivities: (loading) => set({ isLoadingActivities: loading }),
  setOverdueCount: (count) => set({ overdueCount: count }),

  // Segment setters
  setSegments: (segments) => set({ segments }),
  setActiveSegment: (segment) => set({ activeSegment: segment }),
  setLoadingSegments: (loading) => set({ isLoadingSegments: loading }),

  // Deal setters
  setPipelines: (pipelines) => set({ pipelines }),
  setActivePipeline: (pipeline) => set({ activePipeline: pipeline }),
  setDeals: (deals, total) => set({ deals, dealTotal: total }),
  setLoadingDeals: (loading) => set({ isLoadingDeals: loading }),
  setSelectedDeal: (deal) => set({ selectedDeal: deal }),
  setPipelineStats: (stats) => set({ pipelineStats: stats }),
  setDealViewMode: (mode) => set({ dealViewMode: mode }),
  updateDealInList: (deal) =>
    set((state) => ({
      deals: state.deals.map((d) => (d.id === deal.id ? deal : d)),
    })),

  reset: () => set(initialState),
}))
